import {
  Card,
  CardContent,
  CardHeader,
  ErrorBoundary,
  useIntersectionObserver,
} from '@packages/shared';
import { useAtom } from 'jotai';
import { Suspense, useRef } from 'react';
import { useIntl } from 'react-intl';
import {
  LazyCalculator,
  InstallmentCalculatorErrorFallback,
  InstallmentCalculatorSuspenseFallback,
} from '@packages/installment-calculator/src';
import { installmentCollapsed } from '../../../atoms';

type InstallmentCardProps = {
  /**
   * Product price value
   */
  basePrice: number;
  cardID: string;
};

export const InstallmentCard = ({ basePrice, cardID }: InstallmentCardProps) => {
  const calcElement = useRef<HTMLDivElement>(null);
  const { formatMessage } = useIntl();
  const [collapsed] = useAtom(installmentCollapsed);
  const { isIntersecting } = useIntersectionObserver(calcElement, {
    intersectOnce: true,
    threshold: 0,
  });

  return (
    <Card id={cardID} ref={calcElement}>
      <CardHeader
        sx={{ py: 1, px: 1.5, borderBottom: 1, borderColor: 'grey.main' }}
        title={formatMessage({
          id: 'installment-calculator.card-headline',
          defaultMessage: 'Ratenrechner',
        })}
      />
      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
        {isIntersecting && (
          <ErrorBoundary
            fallback={(reset) => <InstallmentCalculatorErrorFallback onCloseHandler={reset} />}
          >
            <Suspense
              fallback={<InstallmentCalculatorSuspenseFallback calculatorLayout="Collapsible" />}
            >
              <LazyCalculator basePrice={basePrice} layout="Collapsible" collapsed={collapsed} />
            </Suspense>
          </ErrorBoundary>
        )}
      </CardContent>
    </Card>
  );
};
